import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Typography,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel
} from '@mui/material';

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

import axios from 'axios';
import mqtt from 'mqtt';

const whiteInputSX = {
  '& label': { color: '#ff9800' },
  '& label.Mui-focused': { color: '#ff9800' },
  '& .MuiInputBase-input': { color: '#ff9800' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.6)' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ff9800' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ff9800' },
};

export default function UpdateLightingMode() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const mqttClient = useRef(null);

  const { id, name, description, ...rest } = state;

  const [modeName, setModeName] = useState(name || '');
  const [modeDescription, setModeDescription] = useState(description || '');

  const [intervals, setIntervals] = useState(
    Array.from({ length: 6 }, (_, i) => ({
      valid: rest[`validT${i + 1}`],
      sH: rest[`sH${i + 1}`],
      sM: rest[`sM${i + 1}`],
      sS: rest[`sS${i + 1}`],
      sbri: rest[`sbri${i + 1}`],
      schn: rest[`schn${i + 1}`],
      eH: rest[`eH${i + 1}`],
      eM: rest[`eM${i + 1}`],
      eS: rest[`eS${i + 1}`],
      ebri: rest[`ebri${i + 1}`],
      echn: rest[`echn${i + 1}`],
    }))
  );

  const SERVER_URL = process.env.REACT_APP_BACKEND_SERVER_URL;

  useEffect(() => {
    mqttClient.current = mqtt.connect('wss://smartlight.bhtelecom.ba:8083');

    mqttClient.current.on('connect', () => {
      console.log('Povezan na MQTT broker');
    });

    return () => mqttClient.current?.end();
  }, []);

  const updateInterval = (index, field, value) => {
    const updated = [...intervals];
    updated[index][field] = value;
    setIntervals(updated);
  };

  const notifyLightingModeChanged = (lightingmodeid) => {
    if (!mqttClient.current) return;

    const payload = { lightingmodeid, type: 'LightingModeChanged', timestamp: Date.now() };

    mqttClient.current.publish(
      '/smartmap/LightingModeChanged',
      JSON.stringify(payload),
      { qos: 1 }
    );
  };

  const validateIntervals = () => {
    // Prvo provjeri da start < end unutar svakog intervala
    for (let i = 0; i < intervals.length; i++) {
      const t = intervals[i];
      if (!t.valid) continue;

      const startSec = Number(t.sH) * 3600 + Number(t.sM) * 60 + Number(t.sS);
      const endSec = Number(t.eH) * 3600 + Number(t.eM) * 60 + Number(t.eS);

      if (startSec >= endSec) {
        return {
          ok: false,
          message: `Unutar intervala ${i + 1} vrijeme početka mora biti prije vremena završetka`
        };
      }
    }

    // Filtriraj samo aktivne intervale i sortiraj po startu
    const active = intervals
      .map((t, i) => ({
        start: Number(t.sH) * 3600 + Number(t.sM) * 60 + Number(t.sS),
        end: Number(t.eH) * 3600 + Number(t.eM) * 60 + Number(t.eS),
        index: i,
        valid: t.valid
      }))
      .filter(x => x.valid)
      .sort((a, b) => a.start - b.start);

    // Provjeri prelazak ili preklapanje
    for (let i = 0; i < active.length - 1; i++) {
      if (active[i].end > active[i + 1].start) {
        return {
          ok: false,
          message: `Interval ${active[i].index + 1} se preklapa s intervalom ${active[i + 1].index + 1}`
        };
      }
    }

    return { ok: true };
  };

  const handleSave = () => {
    const validation = validateIntervals();
    if (!validation.ok) {
      alert(validation.message);
      return; // ne spašavaj ako se intervali preklapaju
    }
    updateAPIData(); // može spasiti jer nema preklapanja
  };

  const updateAPIData = () => {
    const payload = { name: modeName, description: modeDescription };

    intervals.forEach((t, i) => {
      payload[`validT${i + 1}`] = t.valid;
      payload[`sH${i + 1}`] = t.sH;
      payload[`sM${i + 1}`] = t.sM;
      payload[`sS${i + 1}`] = t.sS;
      payload[`sbri${i + 1}`] = t.sbri;
      payload[`schn${i + 1}`] = t.schn;
      payload[`eH${i + 1}`] = t.eH;
      payload[`eM${i + 1}`] = t.eM;
      payload[`eS${i + 1}`] = t.eS;
      payload[`ebri${i + 1}`] = t.ebri;
      payload[`echn${i + 1}`] = t.echn;
    });

    axios
      .put(`${SERVER_URL}/api/lightingmodes/${id}`, payload)
      .then(() => {
        notifyLightingModeChanged(id);
        navigate('/dashboard/LightingModesListAll');
      })
      .catch(err => console.error('Update lighting mode failed', err));
  };

  const chartData = [];

  intervals.forEach((t, i) => {
    if (t.valid) {
      const startSec = Number(t.sH) * 3600 + Number(t.sM) * 60 + Number(t.sS);
      const endSec = Number(t.eH) * 3600 + Number(t.eM) * 60 + Number(t.eS);

      chartData.push({
        timeSec: startSec,
        brightness: t.sbri,
        interval: `Interval ${i + 1} start`,
      });

      chartData.push({
        timeSec: endSec,
        brightness: t.ebri,
        interval: `Interval ${i + 1} end`,
      });
    }
  });

  // sortiramo po vremenu
  chartData.sort((a, b) => a.timeSec - b.timeSec);

  // --- XTicks: svi start i end tickovi ---
  const xTicks = [...new Set(chartData.map(d => d.timeSec))].sort((a, b) => a - b);

  // --- XAxis formatter ---
  const formatSecToHHMMSS = (sec) => {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div style={{ backgroundColor: '#001536', minHeight: '100vh', padding: 20 }}>
      <Grid container justifyContent="center">
        <Card sx={{ maxWidth: 900, width: '100%', backgroundColor: '#00122E', color: '#ff9800' }}>
          <CardHeader sx={{ color: '#ff9800' }} avatar={<Avatar>{modeName?.[0]}</Avatar>} title={modeName} subheader={modeDescription} />
          <CardContent>
            <TextField
              label="Naziv režima"
              value={modeName}
              onChange={(e) => setModeName(e.target.value)}
              fullWidth
              margin="normal"
              sx={whiteInputSX}
            />
            <TextField
              label="Opis"
              value={modeDescription}
              onChange={(e) => setModeDescription(e.target.value)}
              fullWidth
              margin="normal"
              sx={whiteInputSX}
            />

            {intervals.map((t, i) => (
              <Card key={i} sx={{ mt: 3, p: 2, backgroundColor: '#00122E' }}>
                <Typography variant="subtitle1" sx={{ color: '#ff9800', fontWeight: 600 }}>Interval {i + 1}</Typography>
                <FormControlLabel
                  label={<Typography sx={{ color: '#ff9800' }}>Aktivan</Typography>}
                  control={<Switch checked={t.valid} onChange={(e) => updateInterval(i, 'valid', e.target.checked)} />}
                />
                <br />
                <Typography sx={{ color: '#ff9800', fontWeight: 600 }}>
                  Prva komanda
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <TextField label="Hours" type="number" value={t.sH} onChange={(e) => updateInterval(i, 'sH', e.target.value)} fullWidth sx={whiteInputSX} />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField label="Minutes" type="number" value={t.sM} onChange={(e) => updateInterval(i, 'sM', e.target.value)} fullWidth sx={whiteInputSX} />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField label="Seconds" type="number" value={t.sS} onChange={(e) => updateInterval(i, 'sS', e.target.value)} fullWidth sx={whiteInputSX} />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField label="Power %" type="number" value={t.sbri} onChange={(e) => updateInterval(i, 'sbri', e.target.value)} fullWidth sx={whiteInputSX} />
                  </Grid>
                </Grid>
                <br />
                <Typography sx={{ color: '#ff9800', fontWeight: 600 }}>
                  Druga komanda
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <TextField label="Hours" type="number" value={t.eH} onChange={(e) => updateInterval(i, 'eH', e.target.value)} fullWidth sx={whiteInputSX} />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField label="Minutes" type="number" value={t.eM} onChange={(e) => updateInterval(i, 'eM', e.target.value)} fullWidth sx={whiteInputSX} />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField label="Seconds" type="number" value={t.eS} onChange={(e) => updateInterval(i, 'eS', e.target.value)} fullWidth sx={whiteInputSX} />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField label="Power %" type="number" value={t.ebri} onChange={(e) => updateInterval(i, 'ebri', e.target.value)} fullWidth sx={whiteInputSX} />
                  </Grid>
                </Grid>
              </Card>
            ))}

            <Typography variant="h6" sx={{ mt: 4, color: '#ff9800' }}>Power over time %</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid stroke="#444" strokeDasharray="3 3" />
                <XAxis
                  dataKey="timeSec"
                  type="number"
                  domain={['dataMin', 'dataMax']}
                  ticks={xTicks}
                  tickFormatter={formatSecToHHMMSS}
                  interval={0} // svi tickovi
                  tick={{ fontSize: 9, fill: '#ff9800' }} // još manji font
                  angle={-45} // rotacija tickova
                  textAnchor="end"
                  height={60} // povećava prostor za tickove
                  padding={{ left: 10, right: 10 }} // da tickovi ne budu odsječeni
                />


                <YAxis stroke="#ff9800" />
                <Tooltip
                  formatter={(value, name, props) => [`${value}`, props.payload.interval]}
                  labelFormatter={formatSecToHHMMSS}
                />
                <Line
                  type="stepAfter"
                  dataKey="brightness"
                  stroke="#ffeb3b"
                  strokeWidth={2}
                  dot
                />
              </LineChart>
            </ResponsiveContainer>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
              <Button variant="outlined"
                sx={{
                  minWidth: 100,
                  backgroundColor: '#001B44',
                  color: '#ff9800',                // 🔥 tekst orange
                  border: '1px solid #ff9800',     // 🔥 border orange
                  '&:hover': {
                    backgroundColor: '#001B44',
                    border: '1px solid #ffa726',   // 🔥 malo svjetliji hover
                    color: '#ffa726'
                  }
                }}
                onClick={() => navigate(-1)}>
                Odustani
              </Button>

              <Button variant="outlined"
                sx={{
                  minWidth: 100,
                  backgroundColor: '#001B44',
                  color: '#ff9800',                // 🔥 tekst orange
                  border: '1px solid #ff9800',     // 🔥 border orange
                  '&:hover': {
                    backgroundColor: '#001B44',
                    border: '1px solid #ffa726',   // 🔥 malo svjetliji hover
                    color: '#ffa726'
                  }
                }}
                onClick={handleSave}>
                Spasi
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </div >
  );
}
